import axios from 'axios';

// Get base API URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// ALWAYS force HTTPS for non-localhost environments
const isLocalhost = API_BASE_URL.includes('localhost') || API_BASE_URL.includes('127.0.0.1');

// For production environments, explicitly use HTTPS
// For localhost, keep as is
const apiUrl = isLocalhost
  ? API_BASE_URL
  : API_BASE_URL.replace(/^http:\/\//i, 'https://');

console.log(`API URL config: ${isLocalhost ? 'Using localhost: ' + API_BASE_URL : 'Using HTTPS: ' + apiUrl}`);

// Create axios instance with the appropriate base URL
const api = axios.create({
    baseURL: apiUrl,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    // Add a timeout to prevent hanging requests
    timeout: 30000,
});

// Add request interceptor to include token in every request
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
            console.log(`Adding auth token to request: ${config.url}`);
            
            // Debug token information (truncated for security)
            const tokenStart = token.substring(0, 10);
            const tokenEnd = token.substring(token.length - 5);
            console.log(`Token used: ${tokenStart}...${tokenEnd}`);
        } else {
            console.warn(`No auth token found for request: ${config.url}`);
            // Try to redirect to login if no token is available
            if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
                console.warn('No authentication token found, user may need to login again');
            }
        }
        return config;
    },
    (error) => {
        console.error('API request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const status = error.response.status;
            const detail = error.response.data?.detail || 'Unknown error';
            
            if (status === 401) {
                console.error('Authentication error - token might be invalid or expired');
                // Could add automatic logout here if needed
                if (typeof window !== 'undefined') {
                    alert('Your session has expired. Please log in again.');
                    // Redirect to login page after a brief delay
                    setTimeout(() => {
                        window.location.href = '/login';
                    }, 1000);
                }
            } else if (status === 422) {
                console.error('Validation error:', error.response.data);
                // Log more detailed information about the validation error
                if (error.response.data?.detail) {
                    console.error('Validation details:', JSON.stringify(error.response.data.detail));
                }
            } else if (status === 307 || status === 308) {
                // Handle redirects manually to maintain HTTPS
                const redirectUrl = error.response.headers.location;
                if (redirectUrl) {
                    console.log(`Following redirect to: ${redirectUrl}`);
                    // Ensure redirect URL uses HTTPS if not localhost
                    const secureRedirectUrl = isLocalhost
                        ? redirectUrl
                        : redirectUrl.replace(/^http:\/\//i, 'https://');
                    
                    // Clone the original request config
                    const newRequest = {...error.config};
                    newRequest.url = secureRedirectUrl;
                    
                    // Make a new request to the redirect URL
                    return api(newRequest);
                }
            }
            
            console.error(`API error (${status}): ${detail}`);
        } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received from API. This could be a network issue or CORS problem:', error.request);
            
            // Check if this might be a mixed content error
            if (typeof window !== 'undefined' && window.location.protocol === 'https:' && apiUrl.startsWith('http:')) {
                console.error('Possible mixed content error: trying to load HTTP content from HTTPS page');
            }
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error('API request setup error:', error.message);
        }
        
        return Promise.reject(error);
    }
);

// Types for the service
export interface TargetAudienceAttributes {
    age_range?: string[];
    gender?: string[];
    interests?: string[];
    location?: string[];
    income_level?: string[];
    education?: string[];
    occupation?: string[];
}

export enum InputMethod {
    URL = "url",
    BRIEF = "brief"
}

export enum CampaignGoal {
    AWARENESS = "awareness",
    CONSIDERATION = "consideration",
    CONVERSION = "conversion",
    ENGAGEMENT = "engagement",
    TRAFFIC = "traffic",
    LEAD_GENERATION = "lead_generation",
    PRODUCT_LAUNCH = "product_launch",
    BRAND_AWARENESS = "brand_awareness",
    CUSTOMER_RETENTION = "customer_retention",
    MARKET_EXPANSION = "market_expansion"
}

export enum AdFormat {
    SOCIAL_MEDIA_POST = "social_media_post",
    SEARCH_AD = "search_ad",
    DISPLAY_AD = "display_ad",
    CHATBOT_SCRIPT = "chatbot_script",
    EMAIL_NEWSLETTER = "email_newsletter",
    VIDEO_AD = "video_ad"
}

export interface AdVariant {
    id?: number;
    format: AdFormat;
    title?: string;
    content: string;
    call_to_action?: string;
    target_platform?: string;
    additional_metadata?: Record<string, any>;
}

export interface CampaignStrategy {
    main_selling_points: string[];
    target_keywords: string[];
    messaging_approach: string;
    tone_and_voice: string;
    value_proposition: string;
}

export interface AdStrategyRequest {
    input_method: InputMethod;
    product_url?: string;
    campaign_brief?: string;
    campaign_goals: CampaignGoal[];
    target_audience: TargetAudienceAttributes;
    ad_formats: AdFormat[];
    tone?: string;
    keywords?: string[];
}

export interface AdStrategyResponse {
    id: number;
    user_id: number;
    request: AdStrategyRequest;
    strategy: CampaignStrategy;
    ad_variants: AdVariant[];
    created_at: string;
    updated_at?: string;
    status?: string;
    error_message?: string;
}

export interface URLAnalysisResponse {
    title: string;
    description: string;
    keywords: string[];
    main_features: string[];
    pricing_info?: string;
    images?: string[];
    metadata?: Record<string, any>;
}

export const analyzeUrl = async (url: string): Promise<URLAnalysisResponse> => {
    try {
        console.log(`Analyzing URL: ${url}`);
        
        // Validate URL format before sending
        try {
            new URL(url); // Will throw if URL is invalid
        } catch (e) {
            console.error('Invalid URL format:', url);
            throw new Error('Please enter a valid URL including http:// or https://');
        }
        
        // Ensure we have a token before making the request
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Authentication required. Please log in to analyze URLs.');
        }
        
        // Ensure URL is properly encoded
        const encodedUrl = encodeURIComponent(url);
        
        // Use relative URL which will go through Vercel rewrites
        const response = await api.post('/ad-strategist/analyze-url', null, {
            params: { url: encodedUrl }
        });
        
        console.log('URL analysis response:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('Error analyzing URL:', error);
        
        // Enhanced error logging
        if (error.response) {
            const status = error.response.status;
            console.error(`URL analysis failed with status ${status}:`, error.response.data);
            
            // Provide more specific error messages based on status code
            if (status === 401) {
                throw new Error('Authentication required. Please log in to analyze URLs.');
            } else if (status === 422) {
                throw new Error('Invalid URL format or parameters.');
            } else if (status === 500) {
                throw new Error('Server error while analyzing URL. Please try a different URL.');
            } else if (status === 403) {
                throw new Error('You do not have permission to analyze URLs.');
            }
        } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received from server during URL analysis');
            throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
        }
        
        throw error;
    }
};

export const createAdStrategy = async (request: AdStrategyRequest): Promise<AdStrategyResponse> => {
    try {
        // Ensure we have a token before making the request
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('Authentication required. Please log in to create ad strategies.');
        }
        
        console.log('Creating ad strategy with request:', request);
        
        // Use relative URL which will go through Vercel rewrites
        const response = await api.post('/ad-strategist', request);
        console.log('Ad strategy creation successful:', response.data);
        return response.data;
    } catch (error: any) {
        console.error('Error creating ad strategy:', error);
        
        // Handle specific error cases
        if (error.response) {
            const status = error.response.status;
            
            if (status === 401) {
                console.error('Authentication failed when creating ad strategy');
                throw new Error('Authentication required. Your session may have expired.');
            } else if (status === 403) {
                throw new Error('You do not have permission to create ad strategies.');
            } else if (status === 400) {
                const detail = error.response.data?.detail || 'Invalid request';
                throw new Error(`Bad request: ${detail}`);
            }
        } else if (error.request) {
            // The request was made but no response was received
            console.error('No response received from server during ad strategy creation');
            throw new Error('Network error: Unable to connect to the server. Please check if the API is running and accessible.');
        }
        
        throw error;
    }
};

export const getAdStrategies = async (skip = 0, limit = 100): Promise<any[]> => {
    try {
        // Use relative URL which will go through Vercel rewrites
        const response = await api.get('/ad-strategist', {
            params: { skip, limit }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching ad strategies:', error);
        throw error;
    }
};

export const getAdStrategy = async (id: number): Promise<AdStrategyResponse> => {
    try {
        const response = await api.get(`/ad-strategist/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching ad strategy ${id}:`, error);
        throw error;
    }
};

export const trackVariantPerformance = async (
    variantId: number, 
    impressions?: number, 
    clicks?: number
): Promise<any> => {
    // Validate inputs
    if (!variantId || variantId <= 0) {
        const error = new Error(`Invalid variant ID: ${variantId}`);
        console.error(error);
        throw error;
    }

    try {
        console.log(`Sending tracking request for variant ${variantId}:`, { impressions, clicks });
        
        const response = await api.post(`/ad-strategist/variants/${variantId}/performance`, null, {
            params: { 
                impressions: impressions || undefined,
                clicks: clicks || undefined
            }
        });
        
        console.log(`Successfully tracked performance for variant ${variantId}:`, response.data);
        return response.data;
    } catch (error) {
        console.error(`Error tracking variant ${variantId} performance:`, error);
        throw error;
    }
};

export const deleteAdStrategy = async (id: number): Promise<void> => {
    try {
        await api.delete(`/ad-strategist/${id}`);
    } catch (error) {
        console.error(`Error deleting ad strategy ${id}:`, error);
        throw error;
    }
};

export const getDebugVariants = async (strategyId: number): Promise<any> => {
    try {
        const response = await api.get(`/ad-strategist/debug/variants/${strategyId}`);
        console.log(`Debug variants for strategy ${strategyId}:`, response.data);
        return response.data;
    } catch (error) {
        console.error(`Error fetching debug variants for strategy ${strategyId}:`, error);
        throw error;
    }
}; 